import { apiDelete, apiGet, apiPost, apiPut } from "../_apiShim";
import { handleError } from "@/lib/api/errors/ErrorHandler";
import type {
  EnsureNotificationContactsRequest,
  EnsureNotificationContactsResponse,
  GuestNotificationContactLink,
  GuestNotificationContacts,
  NotificationContact,
  NotificationContactList,
  NotificationContactPicker,
  NotificationContactRegistryUpsert,
  ResendVerificationResult,
} from "@/types/NotificationContact";

const BASE_PATH = "/api/v1/events/";

const NO_STORE = { "Cache-Control": "no-store" } as const;

function base(eventId: string): string {
  return `${BASE_PATH}${encodeURIComponent(eventId)}/notification-contacts`;
}

export async function getNotificationContacts(eventId: string): Promise<NotificationContactList> {
  try {
    return await apiGet<NotificationContactList>(base(eventId), { ...NO_STORE }, true);
  } catch (error) {
    return handleError(error);
  }
}

/**
 * Create or edit a registry contact (plan legacy-out Rev. 3). The body is
 * built key by key — the id (edit only), address, label, language — so no
 * `triggers` / `fields` / `cockpitBulk` / `audience` ever reaches the wire,
 * whatever the caller's object carries: main-backend answers any of them
 * with 409 `notificationContactPageOutdated`.
 */
export async function putNotificationContact(
  eventId: string,
  body: NotificationContactRegistryUpsert,
): Promise<NotificationContact> {
  const wire: NotificationContactRegistryUpsert = {
    ...(body.eventNotificationContactId ? { eventNotificationContactId: body.eventNotificationContactId } : {}),
    email: body.email,
    label: body.label,
    language: body.language,
  };
  try {
    return await apiPut<NotificationContact>(base(eventId), wire, { ...NO_STORE }, true);
  } catch (error) {
    return handleError(error);
  }
}

export async function deleteNotificationContact(eventId: string, contactId: string): Promise<void> {
  try {
    await apiDelete<void>(`${base(eventId)}/${encodeURIComponent(contactId)}`, { ...NO_STORE });
  } catch (error) {
    return handleError(error);
  }
}

export async function resendNotificationContactVerification(
  eventId: string,
  contactId: string,
): Promise<ResendVerificationResult> {
  try {
    return await apiPost<ResendVerificationResult>(
      `${base(eventId)}/${encodeURIComponent(contactId)}/resend-verification`,
      {},
      { ...NO_STORE },
      true,
    );
  } catch (error) {
    return handleError(error);
  }
}

/**
 * W4 Hop B (N29) — the guest popup's read: the guest's own contacts and the
 * ones it inherits from its main guest. Never carries guest ids.
 */
export async function getGuestNotificationContacts(
  eventId: string,
  guestId: string,
): Promise<GuestNotificationContacts> {
  try {
    return await apiGet<GuestNotificationContacts>(
      `${base(eventId)}/guests/${encodeURIComponent(guestId)}`,
      { ...NO_STORE },
      true,
    );
  } catch (error) {
    return handleError(error);
  }
}

/** Find-or-create a contact for the address (double opt-in), then link the guest. */
export async function linkGuestNotificationContact(
  eventId: string,
  guestId: string,
  body: GuestNotificationContactLink,
): Promise<NotificationContact> {
  try {
    return await apiPut<NotificationContact>(
      `${base(eventId)}/guests/${encodeURIComponent(guestId)}`,
      { email: body.email, label: body.label, language: body.language },
      { ...NO_STORE },
      true,
    );
  } catch (error) {
    return handleError(error);
  }
}

/** Unlink one guest from one contact (204, idempotent). */
export async function unlinkGuestNotificationContact(
  eventId: string,
  contactId: string,
  guestId: string,
): Promise<void> {
  try {
    await apiDelete<void>(
      `${base(eventId)}/${encodeURIComponent(contactId)}/guests/${encodeURIComponent(guestId)}`,
      { ...NO_STORE },
    );
  } catch (error) {
    return handleError(error);
  }
}

/**
 * The import's bulk find-or-create (contract-b §3). `dryRun: true` counts and
 * writes nothing — the import asks it first and shows the numbers before any
 * contact or confirmation mail exists.
 */
export async function ensureNotificationContacts(
  eventId: string,
  body: EnsureNotificationContactsRequest,
): Promise<EnsureNotificationContactsResponse> {
  try {
    return await apiPost<EnsureNotificationContactsResponse>(
      `${base(eventId)}/ensure`,
      { emails: body.emails, language: body.language, dryRun: body.dryRun },
      { ...NO_STORE },
      true,
    );
  } catch (error) {
    return handleError(error);
  }
}

/**
 * plan arrival-automation Rev. 4 §1.4 — the NOTIFY_CONTACT editor's contact
 * choice (`workflows:read`): the full shape for an in-person OWNER / ADMIN,
 * the reduced "Kontakt N" shape for everyone else. Never an address.
 */
export async function getNotificationContactPicker(eventId: string): Promise<NotificationContactPicker> {
  try {
    return await apiGet<NotificationContactPicker>(`${base(eventId)}/picker`, { ...NO_STORE }, true);
  } catch (error) {
    return handleError(error);
  }
}
